import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const CalificarRespuesta = () => {
  const { formularioId, respuestaId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [formulario, setFormulario] = useState(null);
  const [respuesta, setRespuesta] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [calificaciones, setCalificaciones] = useState({});
  const [comentarios, setComentarios] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [formularioId, respuestaId]);

  const loadData = async () => {
    try {
      const isStudent = user?.rol === 'estudiante';
      console.log('DEBUG: Loading data for formulario:', formularioId, 'respuesta:', respuestaId);
      
      if (isStudent) {
        // Student: use /mi-respuesta endpoint
        const [formularioRes, miRespuestaRes, preguntasRes] = await Promise.all([
          axios.get(`${API}/formularios/${formularioId}`),
          axios.get(`${API}/formularios/${formularioId}/mi-respuesta`),
          axios.get(`${API}/formularios/${formularioId}/preguntas`)
        ]);
        
        setFormulario(formularioRes.data);
        setPreguntas(preguntasRes.data);
        setRespuesta(miRespuestaRes.data);
      } else {
        // Admin/Profesor: use /respuestas endpoint
        const [formularioRes, respuestasRes, preguntasRes] = await Promise.all([
          axios.get(`${API}/formularios/${formularioId}`),
          axios.get(`${API}/formularios/${formularioId}/respuestas`),
          axios.get(`${API}/formularios/${formularioId}/preguntas`)
        ]);
        
        setFormulario(formularioRes.data);
        setPreguntas(preguntasRes.data);
        
        const respuestaEspecifica = respuestasRes.data.find(resp => resp.id === respuestaId);
        if (!respuestaEspecifica) {
          throw new Error(`Respuesta con ID ${respuestaId} no encontrada`);
        }
        setRespuesta(respuestaEspecifica);
        
        // Initialize calificaciones and comentarios (only for grading by admin/profesor)
        const initialCalificaciones = {};
        const initialComentarios = {};
        
        respuestaEspecifica.respuestas?.forEach(resp => {
          initialCalificaciones[resp.pregunta_id] = resp.puntuacion || 0;
          initialComentarios[resp.pregunta_id] = resp.comentario || '';
        });
        
        setCalificaciones(initialCalificaciones);
        setComentarios(initialComentarios);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleCalificacionChange = (preguntaId, valor) => {
    setCalificaciones({
      ...calificaciones,
      [preguntaId]: parseFloat(valor) || 0
    });
  };

  const handleComentarioChange = (preguntaId, comentario) => {
    setComentarios({
      ...comentarios,
      [preguntaId]: comentario
    });
  };

  const handleGuardarCalificacion = async () => {
    setSaving(true);
    try {
      const calificacionData = {
        respuestas: Object.keys(calificaciones).map(preguntaId => ({
          pregunta_id: preguntaId,
          puntuacion: calificaciones[preguntaId],
          comentario: comentarios[preguntaId]
        }))
      };
      
      // Use the working endpoint for grading
      const puntuaciones = {};
      calificacionData.respuestas.forEach(resp => {
        puntuaciones[resp.pregunta_id] = {
          puntuacion: resp.puntuacion,
          comentario: resp.comentario || ""
        };
      });
      
      await axios.put(`${API}/respuestas-formulario/${respuestaId}/calificar`, { puntuaciones });
      
      toast.success('Calificación guardada exitosamente');
      navigate(`/app/formulario/${formularioId}/respuestas`);
    } catch (error) {
      console.error('Error saving calificacion:', error);
      toast.error('Error al guardar calificación');
    } finally {
      setSaving(false);
    }
  };

  const getPreguntaById = (preguntaId) => {
    return preguntas.find(p => p.id === preguntaId);
  };

  const getRespuestaByPreguntaId = (preguntaId) => {
    return respuesta.respuestas?.find(r => r.pregunta_id === preguntaId);
  };

  const calcularPuntuacionTotal = () => {
    return Object.values(calificaciones).reduce((total, puntos) => total + puntos, 0);
  };

  const getPuntuacionMaxima = () => {
    return preguntas.reduce((total, pregunta) => total + pregunta.puntuacion, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando datos...</p>
        </div>
      </div>
    );
  }

  const isStudent = user?.rol === 'estudiante';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 text-blue-600 hover:text-blue-800"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Volver
        </Button>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isStudent ? 'Mi Respuesta: ' : 'Calificar: '}{formulario?.titulo}
            </h1>
            <div className="flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <i className="fas fa-user mr-1"></i>
                {respuesta?.estudiante_nombre}
              </div>
              <div className="flex items-center">
                <i className="fas fa-calendar mr-1"></i>
                {new Date(respuesta?.fecha_envio).toLocaleDateString()}
              </div>
              {!isStudent && (
              <div className="flex items-center">
                <i className="fas fa-star mr-1"></i>
                {calcularPuntuacionTotal().toFixed(1)} / {getPuntuacionMaxima()} puntos
              </div>
              )}
            </div>
          </div>
          {!isStudent && (
          <Badge 
            variant={respuesta?.calificado ? "default" : "secondary"}
            className={respuesta?.calificado ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}
          >
            {respuesta?.calificado ? "Calificado" : "Pendiente"}
          </Badge>
          )}
        </div>
      </div>

      {/* Questions and Responses */}
      <div className="space-y-6">
        {preguntas
          .filter(pregunta => {
            // Excluir preguntas informativas que no requieren calificación
            if (pregunta.tipo === 'parrafo_lectura') return false;
            if (pregunta.tipo === 'link_youtube' && pregunta.video_url) return false;
            if (pregunta.tipo === 'link_wix' && pregunta.wix_url) return false;
            return true;
          })
          .map((pregunta, index) => {
          const respuestaPregunta = getRespuestaByPreguntaId(pregunta.id);
          
          return (
            <Card key={pregunta.id} className="shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-start justify-between">
                  <div className="flex items-start">
                    <span className="text-blue-600 mr-2">{index + 1}.</span>
                    <div 
                      className="prose prose-sm max-w-none text-gray-900 prose-p:my-1 prose-headings:my-1 prose-ul:my-1 prose-ol:my-1 prose-strong:text-gray-900 prose-em:text-gray-800"
                      dangerouslySetInnerHTML={{ __html: pregunta.titulo }}
                    />
                  </div>
                  <Badge variant="outline" className="text-xs ml-4 shrink-0">
                    {pregunta.puntuacion} pts máximo
                  </Badge>
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Student Response */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <Label className="font-medium text-gray-700 mb-2 block">
                    Respuesta del estudiante:
                  </Label>
                  <div className="bg-white p-3 rounded border">
                    {respuestaPregunta?.respuesta || 'Sin respuesta'}
                  </div>
                </div>

                {/* Correct Answer (if exists) */}
                {pregunta.respuesta_correcta && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <Label className="font-medium text-green-700 mb-2 block">
                      Respuesta correcta:
                    </Label>
                    <div className="bg-white p-3 rounded border">
                      {pregunta.respuesta_correcta}
                    </div>
                  </div>
                )}

                {/* Grading Section - Only for admin/profesor */}
                {!isStudent && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                  <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300">
                    <Label htmlFor={`puntuacion_${pregunta.id}`} className="font-medium text-yellow-900 flex items-center">
                      <i className="fas fa-star text-yellow-600 mr-2"></i>
                      Puntuación obtenida
                    </Label>
                    <div className="flex items-center space-x-2 mt-2">
                      <Input
                        id={`puntuacion_${pregunta.id}`}
                        type="number"
                        min="0"
                        max={pregunta.puntuacion}
                        step="0.1"
                        value={calificaciones[pregunta.id] || 0}
                        onChange={(e) => handleCalificacionChange(pregunta.id, e.target.value)}
                        className="w-24 bg-white border-2 border-yellow-400 focus:ring-2 focus:ring-yellow-500 font-bold text-lg"
                      />
                      <span className="text-sm text-gray-700 font-medium">
                        / {pregunta.puntuacion} pts
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor={`comentario_${pregunta.id}`} className="font-medium">
                      Comentario (opcional)
                    </Label>
                    <Textarea
                      id={`comentario_${pregunta.id}`}
                      placeholder="Retroalimentación para el estudiante..."
                      value={comentarios[pregunta.id] || ''}
                      onChange={(e) => handleComentarioChange(pregunta.id, e.target.value)}
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Save Button - Only for admin/profesor */}
      {!isStudent && (
      <div className="flex justify-center pt-6">
        <Button
          onClick={handleGuardarCalificacion}
          className="instituto-gradient text-white px-8 py-3"
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Guardando...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Guardar Calificación
            </>
          )}
        </Button>
      </div>
      )}

      {/* Summary */}
      {!isStudent && (
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-900 mb-2">
              {calcularPuntuacionTotal().toFixed(1)} / {getPuntuacionMaxima()} puntos
            </div>
            <div className="text-blue-700">
              Puntuación: {((calcularPuntuacionTotal() / getPuntuacionMaxima()) * 100).toFixed(1)}%
            </div>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default CalificarRespuesta;